import { useState, useRef, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';
import { Telemetry } from '@/lib/telemetry';
import { startTimer, endTimer } from '@/lib/telemetry-timers';
import type { ScanDocError, DurableTaskKind, TaskStatus } from '@/types/api';

/**
 * Compute the user-facing error text for a failed task (A2F-1115).
 *
 * Surfaces the worker's `lastError` ONLY when it carries a curated
 * `errorCode` — those are the anticipated, human-safe messages (SPO
 * misconfig, Graph auth, etc.). The worker catch-all records raw
 * `"<ClassName>: <message>"` text (internal paths/SQL) with NO code, which
 * must NOT be rendered — matching ribbon.py's deliberate withholding of raw
 * `last_error`. Uncoded failures fall back to per-document errors, then a
 * generic message. Extracted as a pure function so the gating invariant is
 * unit-testable without mounting the hook.
 */
export function failureMessage(status: Pick<TaskStatus, 'errorCode' | 'lastError' | 'progress'>): string {
  return (
    (status.errorCode ? status.lastError : null)
    ?? status.progress.errors?.[0]?.message
    ?? 'Task failed on the server. You can retry.'
  );
}

const BASE_POLL_INTERVAL_MS = 1500;
/** Polling interval used once a long-running ingest job is mid-flight and
 * progress hasn't moved for a while — small enough to feel responsive,
 * large enough to keep the per-minute request count tiny over a multi-hour
 * ingest. A2F-771. */
const SLOW_POLL_INTERVAL_MS = 5000;
/** After this many seconds without a progress change, drop to the slow
 * poll interval. */
const SLOW_POLL_AFTER_IDLE_SECONDS = 60;

/** Tolerance for transient poll failures (404, network blip).
 * Short LLM scans give up fast because losing them is cheap (re-scan).
 * Durable ingest jobs tolerate ~30s of blips since the job is safe on
 * the worker — losing the UI handle on a still-running ingest is what
 * A2F-771 explicitly fixes. */
const MAX_FAILURES_SHORT = 3;
const MAX_FAILURES_DURABLE = 20;

const DURABLE_TASK_KINDS: ReadonlySet<DurableTaskKind> = new Set([
  'ingest_full',
  'rescan_submission',
  'discover_new_submissions',
  'study_profile_scan',
  'substance_profile_scan',
]);

function isDurableTaskType(taskType: string | undefined | null): boolean {
  if (!taskType) return false;
  return DURABLE_TASK_KINDS.has(taskType as DurableTaskKind);
}

export interface ScanTaskState {
  isScanning: boolean;
  taskId: string | null;
  totalDocs: number;
  completedDocs: number;
  currentDocument: string;
  failedDocs: ScanDocError[];
  stoppedEarly: boolean;
  stoppedMessage: string;
  /** Non-empty when the task failed or was lost (server restart, network). */
  error: string;
  /** A2F-771: most recently completed submission folder name, if surfaced
   * by the backend. */
  lastCompletedSubmission: string | null;
  /** A2F-771: optional ETA in seconds, if the worker computed one. */
  etaSeconds: number | null;
}

const INITIAL_STATE: ScanTaskState = {
  isScanning: false,
  taskId: null,
  totalDocs: 0,
  completedDocs: 0,
  currentDocument: '',
  failedDocs: [],
  stoppedEarly: false,
  stoppedMessage: '',
  error: '',
  lastCompletedSubmission: null,
  etaSeconds: null,
};

export function useScanTask<T>(feature?: string, onComplete?: () => void, onFailed?: () => void) {
  const [taskState, setTaskState] = useState<ScanTaskState>(INITIAL_STATE);
  const [result, setResult] = useState<T | null>(null);
  // Recursive setTimeout — not setInterval — so each tick recomputes the
  // poll interval based on the current idle-since-progress duration.
  // setInterval would lock us to the interval chosen at startTask time
  // and the slow-poll downgrade after 60s of idle would never fire.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);
  // Generation counter incremented on every startTask. The poll closure
  // captures its generation and bails after the await if a newer
  // startTask has superseded it. Prevents two poll loops from running
  // concurrently when startTask is called while a previous poll's HTTP
  // request is in-flight (the in-flight fetch can't be aborted, so
  // without the gen check the old closure would re-arm itself after
  // its fetch resolves).
  const generationRef = useRef(0);
  const taskIdRef = useRef<string | null>(null);
  const featureRef = useRef(feature);
  // Latest-ref pattern (A2F-508): store callbacks in refs so the long-lived
  // poll closure (created once per startTask, resolves seconds-to-hours later)
  // always invokes the most recent onComplete/onFailed rather than the stale
  // closure captured at startTask-memoization time.
  const onCompleteRef = useRef(onComplete);
  const onFailedRef = useRef(onFailed);
  const consecutiveFailures = useRef(0);
  // Track whether we've identified the task as durable yet, plus the
  // timestamp at which visible progress last advanced. Both flow into
  // the polling-interval decision (A2F-771).
  const isDurableRef = useRef(false);
  const lastProgressKey = useRef<string>('');
  const lastProgressAtRef = useRef<number>(Date.now());

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const computePollIntervalMs = useCallback(() => {
    if (!isDurableRef.current) return BASE_POLL_INTERVAL_MS;
    const idleSeconds = (Date.now() - lastProgressAtRef.current) / 1000;
    return idleSeconds > SLOW_POLL_AFTER_IDLE_SECONDS
      ? SLOW_POLL_INTERVAL_MS
      : BASE_POLL_INTERVAL_MS;
  }, []);

  const scheduleNextPoll = useCallback((handler: () => void) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!isPollingRef.current) return;
    timeoutRef.current = setTimeout(handler, computePollIntervalMs());
  }, [computePollIntervalMs]);

  const startTask = useCallback((taskId: string, fileCount?: number) => {
    stopPolling();
    // Bump the generation BEFORE any other state so an in-flight poll
    // from a previous startTask sees a mismatched gen the moment its
    // fetch resolves and bails out cleanly.
    const myGen = ++generationRef.current;
    taskIdRef.current = taskId;
    consecutiveFailures.current = 0;
    isDurableRef.current = false;
    lastProgressKey.current = '';
    lastProgressAtRef.current = Date.now();
    isPollingRef.current = true;
    setResult(null);
    setTaskState({
      ...INITIAL_STATE,
      isScanning: true,
      taskId,
    });

    const feat = featureRef.current;
    if (feat) {
      startTimer(`scan_${feat}`);
      Telemetry.reviewScanStarted({ feature: feat, file_count: fileCount ?? 0 });
    }

    // True while this closure is still the active poll loop for the
    // hook. Captures myGen + checks the shared isPollingRef so we bail
    // on both supersession (new startTask) AND stop (cancel / unmount).
    const isAlive = () => myGen === generationRef.current && isPollingRef.current;

    const poll = async () => {
      try {
        const status = await api.tasks.get(taskId);
        // If a newer startTask happened while our fetch was in flight,
        // or the hook was cancelled/unmounted, do not touch any shared
        // state — the new loop owns it now.
        if (!isAlive()) return;
        consecutiveFailures.current = 0;

        // Detect durable kind from server response on first successful poll.
        // The hook self-tunes its tolerance + polling cadence based on this
        // — no caller has to pass a taskKind explicitly.
        isDurableRef.current = isDurableTaskType(status.taskType);

        // Track whether visible progress moved between polls. Used to drop
        // the cadence once we're mid-stream and nothing has changed for a
        // while — saves polling traffic over multi-hour ingests.
        const progressKey = `${status.progress.completed}/${status.progress.total}/${status.progress.currentDocument}`;
        if (progressKey !== lastProgressKey.current) {
          lastProgressKey.current = progressKey;
          lastProgressAtRef.current = Date.now();
        }

        setTaskState((s) => ({
          ...s,
          totalDocs: status.progress.total,
          completedDocs: status.progress.completed,
          currentDocument: status.progress.currentDocument,
          failedDocs: status.progress.errors,
          lastCompletedSubmission: status.progress.lastCompletedSubmission ?? null,
          etaSeconds: status.progress.etaSeconds ?? null,
          stoppedEarly: status.result?.stoppedEarly === true,
          stoppedMessage:
            status.status === 'cancelled'
              ? 'Cancelled by user'
              : status.result?.stoppedEarly
                ? String((status.result?.errors as Array<{message?: string}> | undefined)?.[0]?.message ?? 'Scan stopped early')
                : '',
        }));

        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          stopPolling();

          // A2F-1115: gated failure copy — see failureMessage() above.
          const errorMsg = status.status === 'failed' ? failureMessage(status) : '';
          setTaskState((s) => ({ ...s, isScanning: false, error: errorMsg }));

          const feat = featureRef.current;
          if (feat) {
            const durationMs = endTimer(`scan_${feat}`);
            if (status.status === 'completed') {
              Telemetry.reviewScanCompleted({
                feature: feat,
                file_count: status.progress.total,
                finding_count: (status.result as any)?.summary?.inconsistent ?? (status.result as any)?.summary?.total ?? 0,
                duration_ms: durationMs,
              });
            } else if (status.status === 'failed') {
              Telemetry.reviewScanFailed({
                feature: feat,
                error_message: status.progress.errors?.[0]?.message ?? 'Scan failed',
              });
            } else if (status.status === 'cancelled') {
              Telemetry.reviewScanCancelled({ feature: feat });
            }
          }

          if (status.status === 'completed' && status.result) {
            setResult(status.result as T);
            onCompleteRef.current?.();
          } else if (status.status === 'failed') {
            setResult(null);
            onFailedRef.current?.();
          }
          return;
        }
      } catch {
        // Network error or 404 (task lost after server restart).
        // Same supersession guard as the success path: a stale closure
        // must not bump the shared failure counter or fire onFailed for
        // a task that the user has already moved past.
        if (!isAlive()) return;
        // Tolerate longer for durable ingest jobs — A2F-771 explicitly
        // de-fragilises this path for the multi-hour ALLN-346 case.
        consecutiveFailures.current += 1;
        const limit = isDurableRef.current ? MAX_FAILURES_DURABLE : MAX_FAILURES_SHORT;
        if (consecutiveFailures.current >= limit) {
          stopPolling();
          setTaskState((s) => ({
            ...s,
            isScanning: false,
            error: 'Lost connection to the server. The operation may still be running — try refreshing the page.',
          }));
          onFailedRef.current?.();
          return;
        }
      }
      // Final supersession check before re-arming — the success/catch
      // blocks above might have ended without taking a terminal branch
      // (e.g. status='running' and no transport error), so guard once
      // more before adding ourselves back to the timer.
      if (!isAlive()) return;
      // Re-arm with a freshly-computed interval. After 60s of idle
      // progress on a durable ingest the next tick fires at 5s instead
      // of 1.5s — that's the cadence drop the slow-poll constants exist
      // for.
      scheduleNextPoll(poll);
    };

    scheduleNextPoll(poll);
  }, [stopPolling, scheduleNextPoll]);

  const cancel = useCallback(async () => {
    const taskId = taskIdRef.current;
    if (taskId) {
      try {
        await api.tasks.cancel(taskId);
      } catch {
        // Task may already be done
      }
    }
    stopPolling();
    setTaskState((s) => ({ ...s, isScanning: false }));
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    taskIdRef.current = null;
    setTaskState(INITIAL_STATE);
    setResult(null);
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Keep callback refs current after every render (no dep array = runs after
  // every render). This is the "latest ref" pattern: the poll loop reads
  // ref.current at call time, so it always gets the most recent closure even
  // though startTask is memoized and the poll closure is created only once per
  // startTask call. A2F-508.
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onFailedRef.current = onFailed;
  });

  return { taskState, startTask, cancel, reset, result };
}
